import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth, TenantData } from '../contexts/AuthContext';
import { Button, Label, TextInput, Alert, Modal, Select, Spinner } from 'flowbite-react';
import { HiMail, HiLockClosed, HiUser, HiPhone, HiCheckCircle, HiInformationCircle, HiLocationMarker, HiHome, HiUserGroup, HiOfficeBuilding } from 'react-icons/hi';
import { MapContainer, TileLayer, Marker, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix para los iconos de Leaflet
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

// Componente para actualizar el mapa
function MapUpdater({ center, zoom }: { center: [number, number], zoom: number }) {
  const map = useMap();
  useEffect(() => {
    map.setView(center, zoom);
  }, [center, zoom, map]);
  return null;
}

// Componente para manejar clicks en el mapa
function MapClickHandler({ onMapClick }: { onMapClick: (e: L.LeafletMouseEvent) => void }) {
  useMapEvents({ click: onMapClick });
  return null;
}

// Función para capitalizar texto
const capitalizeWords = (str: string) => {
  return str.replace(/\b\w/g, char => char.toUpperCase());
};

export default function Register() {
  const navigate = useNavigate();
  const { signUp, signInWithGoogle } = useAuth();
  const [step, setStep] = useState(1); // 1: Datos básicos, 2: Información adicional si corresponde
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    name: '',
    phone: '',
    userType: 'personal' as 'personal' | 'familiar' | 'comunidad' | 'empresa',
  });
  const [tenantData, setTenantData] = useState<TenantData>({
    name: '',
    address: '',
    description: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [mapCenter, setMapCenter] = useState<[number, number]>([-33.4489, -70.6693]);
  const [mapZoom, setMapZoom] = useState(10);
  const [markerPosition, setMarkerPosition] = useState<[number, number] | null>(null);
  const geocodeTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastGeocodedAddressRef = useRef<string>('');

  // Geocodificar dirección automáticamente
  const geocodeAddress = useCallback(async (address: string) => {
    if (!address.trim() || address.trim().length < 5) return;

    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(address)}&limit=1`,
        {
          headers: {
            'User-Agent': 'DomusSecuraApp/1.0 (haliagav@gmail.com)',
          },
        }
      );
      const data = await response.json();

      if (data && data.length > 0) {
        const { lat, lon } = data[0];
        const newLat = parseFloat(lat);
        const newLon = parseFloat(lon);
        setMapCenter([newLat, newLon]);
        setMarkerPosition([newLat, newLon]);
        setTenantData(prev => ({ ...prev, latitude: lat, longitude: lon }));
        setMapZoom(17);
        lastGeocodedAddressRef.current = address;
      }
    } catch (error) {
      console.error('Error geocodificando:', error);
    }
  }, []);

  // Efecto para geocodificar automáticamente con debounce
  useEffect(() => {
    if (geocodeTimeoutRef.current) {
      clearTimeout(geocodeTimeoutRef.current);
    }

    const addressTrimmed = tenantData.address?.trim() || '';
    if (step === 2 && formData.userType !== 'personal' && addressTrimmed.length > 5 && addressTrimmed !== lastGeocodedAddressRef.current) {
      geocodeTimeoutRef.current = setTimeout(() => {
        geocodeAddress(addressTrimmed);
      }, 1000);
    }

    return () => {
      if (geocodeTimeoutRef.current) {
        clearTimeout(geocodeTimeoutRef.current);
      }
    };
  }, [tenantData.address, step, formData.userType, geocodeAddress]);

  const handleMapClick = (e: L.LeafletMouseEvent) => {
    const { lat, lng } = e.latlng;
    setMarkerPosition([lat, lng]);
    setMapCenter([lat, lng]);
    setTenantData(prev => ({ ...prev, latitude: lat.toString(), longitude: lng.toString() }));
  };

  const handleContinueStep1 = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!formData.name.trim()) {
      setError('El nombre es requerido');
      return;
    }

    if (!formData.email.trim()) {
      setError('El email es requerido');
      return;
    }

    if (formData.password.length < 6) {
      setError('La contraseña debe tener al menos 6 caracteres');
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError('Las contraseñas no coinciden');
      return;
    }

    // Si el tipo NO es personal, ir al paso 2
    if (formData.userType !== 'personal') {
      setStep(2);
    } else {
      // Si es personal, registrar directamente
      handleSubmit();
    }
  };

  const handleSubmit = async () => {
    setError('');
    setLoading(true);

    try {
      const tenantDataToSend: TenantData | undefined = 
        formData.userType !== 'personal' && tenantData.name
          ? {
              name: capitalizeWords(tenantData.name),
              address: tenantData.address ? capitalizeWords(tenantData.address) : undefined,
              description: tenantData.description ? capitalizeWords(tenantData.description) : undefined,
              latitude: tenantData.latitude,
              longitude: tenantData.longitude,
            }
          : undefined;

      await signUp(
        formData.email,
        formData.password,
        capitalizeWords(formData.name),
        formData.phone,
        formData.userType,
        tenantDataToSend
      );

      setShowSuccessModal(true);
      setLoading(false);

      setTimeout(() => {
        setShowSuccessModal(false);
        navigate('/waiting-approval');
      }, 3000);
    } catch (err: any) {
      const errorMessage = err.message || 'Error al registrarse';
      setError(errorMessage);
      setLoading(false);
    }
  };

  const handleBackToStep1 = () => {
    setStep(1);
    setError('');
  };


  const getTypeDescription = (type: string) => {
    switch (type) {
      case 'personal':
        return 'Uso personal e individual';
      case 'familiar':
        return 'Para tu familia o grupo pequeño (obtienes rol de administrador)';
      case 'comunidad':
        return 'Para comunidades, condominios o edificios (obtienes rol de administrador)';
      case 'empresa':
        return 'Para empresas u organizaciones (obtienes rol de administrador)';
      default:
        return '';
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 px-4 py-12">
      <div className="w-full max-w-4xl">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-8 text-center">
            <div className="flex justify-center mb-4">
              <img 
                src="/logo.png" 
                alt="Domus Secura" 
                className="h-16 w-16 object-contain bg-white rounded-lg p-2 shadow-lg"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.src = '/logo.svg';
                }}
              />
            </div>
            <h1 className="text-3xl font-bold text-white mb-2">Crear Cuenta</h1>
            <p className="text-blue-100">Regístrate en Domus Secura</p>
          </div>

          {/* Content */}
          <div className="p-6 space-y-6">
            {/* Indicador de pasos */}
            {formData.userType !== 'personal' && (
              <div className="flex items-center justify-center gap-2 mb-4">
                <div className={`flex items-center gap-2 px-4 py-2 rounded-lg ${step === 1 ? 'bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300' : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'}`}>
                  <span className="font-semibold">1</span>
                  <span className="text-sm">Datos Personales</span>
                </div>
                <div className="w-8 h-0.5 bg-gray-300 dark:bg-gray-600"></div>
                <div className={`flex items-center gap-2 px-4 py-2 rounded-lg ${step === 2 ? 'bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300' : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'}`}>
                  <span className="font-semibold">2</span>
                  <span className="text-sm">Información {formData.userType === 'familiar' ? 'Familiar' : formData.userType === 'comunidad' ? 'de la Comunidad' : 'de la Empresa'}</span>
                </div>
              </div>
            )}

            {error && (
              <Alert color="failure" className="mb-4">
                <span className="font-medium">{error}</span>
              </Alert>
            )}

            {/* Paso 1: Datos Básicos */}
            {step === 1 && (
              <form onSubmit={handleContinueStep1} className="space-y-4">
                <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg mb-4">
                  <div className="flex items-start gap-3">
                    <HiInformationCircle className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
                    <div className="text-sm text-blue-700 dark:text-blue-300">
                      <p className="font-semibold mb-1">Selecciona tu tipo de cuenta</p>
                      <p>El tipo determina las funcionalidades y permisos que tendrás. Si eliges Familiar, Comunidad o Empresa, obtendrás permisos de administrador automáticamente.</p>
                    </div>
                  </div>
                </div>

                <div>
                  <Label htmlFor="userType">Tipo de Cuenta</Label>
                  <Select
                    id="userType"
                    value={formData.userType}
                    onChange={(e) => setFormData({ ...formData, userType: e.target.value as any })}
                    className="mt-1"
                    required
                  >
                    <option value="personal">👤 Personal</option>
                    <option value="familiar">🏠 Familiar</option>
                    <option value="comunidad">👥 Comunidad</option>
                    <option value="empresa">🏢 Empresa</option>
                  </Select>
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    {getTypeDescription(formData.userType)}
                  </p>
                </div>

                <div>
                  <Label htmlFor="name">Nombre Completo</Label>
                  <TextInput
                    id="name"
                    type="text"
                    icon={HiUser}
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    placeholder="Juan Pérez"
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label htmlFor="phone">Teléfono <span className="text-gray-400 dark:text-gray-500 text-sm">(opcional)</span></Label>
                  <TextInput
                    id="phone"
                    type="tel"
                    icon={HiPhone}
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    placeholder="+56912345678"
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label htmlFor="email">Email</Label>
                  <TextInput
                    id="email"
                    type="email"
                    icon={HiMail}
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value.toLowerCase() })}
                    required
                    placeholder="tu@email.com"
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label htmlFor="password">Contraseña</Label>
                  <TextInput
                    id="password"
                    type="password"
                    icon={HiLockClosed}
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    required
                    minLength={6}
                    placeholder="••••••••"
                    className="mt-1"
                  />
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    Mínimo 6 caracteres
                  </p>
                </div>

                <div>
                  <Label htmlFor="confirmPassword">Confirmar Contraseña</Label>
                  <TextInput
                    id="confirmPassword"
                    type="password"
                    icon={HiLockClosed}
                    value={formData.confirmPassword}
                    onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                    required
                    minLength={6}
                    placeholder="••••••••"
                    className="mt-1"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-blue-600 hover:bg-blue-700"
                  size="lg"
                >
                  {formData.userType === 'personal' ? (
                    loading ? 'Creando cuenta...' : 'Registrarse'
                  ) : (
                    'Continuar'
                  )}
                </Button>
              </form>
            )}

            {/* Paso 2: Información del Tenant */}
            {step === 2 && (
              <form onSubmit={(e) => { e.preventDefault(); handleSubmit(); }} className="space-y-4">
                <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg mb-4">
                  <div className="flex items-start gap-3">
                    <HiInformationCircle className="w-5 h-5 text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" />
                    <div className="text-sm text-green-700 dark:text-green-300">
                      <p className="font-semibold mb-1">
                        {formData.userType === 'familiar' || formData.userType === 'comunidad' || formData.userType === 'empresa' 
                          ? '¡Importante! Obtendrás permisos de administrador automáticamente.' 
                          : 'Completa la información de tu tenant'}
                      </p>
                      <p>Esta información se usará para crear tu espacio en la plataforma. Podrás editarla después.</p>
                    </div>
                  </div>
                </div>

                <div>
                  <Label htmlFor="tenantName">
                    Nombre {formData.userType === 'familiar' ? 'del Grupo Familiar' : formData.userType === 'comunidad' ? 'de la Comunidad' : 'de la Empresa'}
                  </Label>
                  <TextInput
                    id="tenantName"
                    type="text"
                    icon={formData.userType === 'familiar' ? HiHome : formData.userType === 'comunidad' ? HiUserGroup : HiOfficeBuilding}
                    value={tenantData.name}
                    onChange={(e) => setTenantData({ ...tenantData, name: capitalizeWords(e.target.value) })}
                    required
                    placeholder={formData.userType === 'familiar' ? 'Ej: Familia Pérez' : formData.userType === 'comunidad' ? 'Ej: Condominio Las Flores' : 'Ej: Empresa XYZ Ltda.'}
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label htmlFor="tenantAddress">Dirección <span className="text-gray-400 dark:text-gray-500 text-sm">(opcional)</span></Label>
                  <TextInput
                    id="tenantAddress"
                    type="text"
                    icon={HiLocationMarker}
                    value={tenantData.address || ''}
                    onChange={(e) => setTenantData({ ...tenantData, address: capitalizeWords(e.target.value) })}
                    placeholder="Ej: Av. Principal 123, Santiago"
                    className="mt-1"
                  />
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    El mapa se actualizará automáticamente mientras escribes
                  </p>
                </div>

                {tenantData.address && (
                  <div className="h-64 w-full rounded-lg overflow-hidden border border-gray-300 dark:border-gray-600">
                    <MapContainer
                      center={mapCenter}
                      zoom={mapZoom}
                      style={{ height: '100%', width: '100%' }}
                      whenReady={() => {
                        // El mapa se ajustará automáticamente
                      }}
                    >
                      <TileLayer
                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                      />
                      {markerPosition && (
                        <Marker position={markerPosition} draggable={true} />
                      )}
                      <MapUpdater center={mapCenter} zoom={mapZoom} />
                      <MapClickHandler onMapClick={handleMapClick} />
                    </MapContainer>
                  </div>
                )}

                <div>
                  <Label htmlFor="tenantDescription">Descripción <span className="text-gray-400 dark:text-gray-500 text-sm">(opcional)</span></Label>
                  <TextInput
                    id="tenantDescription"
                    type="text"
                    icon={HiInformationCircle}
                    value={tenantData.description || ''}
                    onChange={(e) => setTenantData({ ...tenantData, description: capitalizeWords(e.target.value) })}
                    placeholder={`Información adicional sobre tu ${formData.userType === 'familiar' ? 'grupo familiar' : formData.userType === 'comunidad' ? 'comunidad' : 'empresa'}`}
                    className="mt-1"
                  />
                </div>

                <div className="flex gap-3">
                  <Button
                    type="button"
                    color="gray"
                    onClick={handleBackToStep1}
                    className="flex-1 text-gray-900 dark:text-gray-200 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600"
                    disabled={loading}
                  >
                    Atrás
                  </Button>
                  <Button
                    type="submit"
                    disabled={loading || !tenantData.name.trim()}
                    className="flex-1 bg-blue-600 hover:bg-blue-700"
                    size="lg"
                  >
                    {loading ? (
                      <>
                        <Spinner size="sm" className="mr-2" />
                        Creando cuenta...
                      </>
                    ) : (
                      'Registrarse'
                    )}
                  </Button>
                </div>
              </form>
            )}

            {/* Separador solo si estamos en paso 1 */}
            {step === 1 && (
              <>
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-gray-300 dark:border-gray-600"></div>
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="px-2 bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400">O continúa con</span>
                  </div>
                </div>

                <Button
                  onClick={async () => {
                    setError('');
                    setLoading(true);
                    try {
                      await signInWithGoogle('signup');
                    } catch (err: any) {
                      setError(err.message || 'Error al registrarse con Google');
                      setLoading(false);
                    }
                  }}
                  disabled={loading}
                  color="gray"
                  className="w-full text-gray-900 dark:text-gray-200 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600"
                  size="lg"
                >
                  <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                    <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                    <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                    <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                    <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                  </svg>
                  Continuar con Google
                </Button>
              </>
            )}

            <p className="text-center text-sm text-gray-600 dark:text-gray-400">
              ¿Ya tienes cuenta?{' '}
              <Link to="/login" className="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 hover:underline font-semibold">
                Inicia sesión
              </Link>
            </p>
          </div>
        </div>
      </div>

      {/* Modal de Éxito */}
      <Modal show={showSuccessModal} onClose={() => {}} size="md">
        <div className="p-6 bg-white dark:bg-gray-800 text-center">
          <HiCheckCircle className="mx-auto mb-4 h-16 w-16 text-green-500" />
          <h3 className="mb-2 text-2xl font-bold text-gray-900 dark:text-white">
            ¡Registro Exitoso!
          </h3>
          <div className="space-y-3 mb-5">
            <p className="text-base text-gray-500 dark:text-gray-400">
              Hemos enviado un email de confirmación a <strong className="text-gray-900 dark:text-white">{formData.email}</strong>.
            </p>
            {(formData.userType === 'familiar' || formData.userType === 'comunidad' || formData.userType === 'empresa') && (
              <div className="bg-green-50 dark:bg-green-900/20 p-3 rounded-lg">
                <p className="text-sm text-green-700 dark:text-green-300 font-semibold">
                  ✅ Obtuviste permisos de administrador automáticamente
                </p>
              </div>
            )}
            <div className="flex items-start gap-3 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
              <HiInformationCircle className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
              <div className="text-left">
                <p className="text-sm font-semibold text-blue-900 dark:text-blue-200 mb-1">
                  Próximos pasos:
                </p>
                <ul className="text-sm text-blue-700 dark:text-blue-300 space-y-1">
                  <li>• Verifica tu correo electrónico</li>
                  <li>• Espera la aprobación del administrador</li>
                  <li>• Te notificaremos cuando tu cuenta sea activada</li>
                </ul>
              </div>
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Redirigiendo a la pantalla de espera...
            </p>
          </div>
        </div>
      </Modal>
    </div>
  );
}
